import { AppHelper } from "../appHelper.js";
import { KeyValuePair } from "../commonInterfaces.js";
import { AppBaseProps, Entity, CommonApp, Context } from "../core/components/commonApp.js";
import { Grid } from "../core/components/grid.js";
import { EditableGrid, field, InGridButtonClickParams, InGridToolBarButton, Schema } from "../core/components/editableGrid/editableGrid.js";
import { ToolBarInGridButtonType, ToolBarStandardButton, ToolBarStandardButtonType } from "../core/components/toolBar.js";
import { Helper } from "../core/util/helper.js";
import { HTTPService, JsonResultClass } from "../core/util/httpManager.js";
import { Config } from "../core/config/config.js";
import { SheetEventType } from "../../siteManagement/sheet.js";
import { General } from "../../siteManagement/general.js";
import { NotificationHelper } from "../core/util/notificationHelper.js";
import { DialogHelper } from "../core/util/dialogHelper.js";
import React from "react";

interface WarehouseAppContext extends Context {
    storageSpaceStates: KeyValuePair<number, string>[];
}

export interface WarehouseAppProps extends AppBaseProps {
    context: WarehouseAppContext;
    entity: Warehouse;
}
export class WarehouseApp extends React.Component<WarehouseAppProps, any>{
    private common = new AppHelper();
    constructor(props: WarehouseAppProps) {
        super(props);

        this.common.enableHooks = false;
        this.common.init(this);
    }

    private handleAddressChange = async (adrNr: string) => {
        if (!Helper.isNullOrEmpty(adrNr)) {
            const { Item1, Item2 } = await HTTPService.get(`Warehouse/GetCoordinates?adrNr=${adrNr}`)
            this.handleFieldChange({ model: "entity.Latitude" }, Item1);
            this.handleFieldChange({ model: "entity.Longitude" }, Item2);
        }
    }

    private handleFieldChange = (field: { model: string }, value, dataItem = null, isInit = false) => {
        switch (field.model) {
            case "entity.AdrNr":
                this.handleAddressChange(value)
                break;

        }
        if (!isInit)
            this.common.handleFieldChange(field, value, dataItem);
    }

    renderGrid = () => {
        const onChange = (data: StorageSpace[]) => {
            this.handleFieldChange({ model: "entity.StorageSpaces" }, data);
        }
        return <StorageSpacesGrid data={this.props.entity.StorageSpaces} states={this.props.context.storageSpaceStates} onChange={onChange} lagerId={this.props.entity.Id} />
    }

    renderResources = () => {
        const onChange = (resources: KeyValuePair<string, string>[]) => {
            this.handleFieldChange({ model: "entity.Resources" }, resources);
        }
        return <Resources resources={this.props.entity.Resources} onChange={onChange} />
    }


    private validate = () => {
        if (!this.common.validate()) {
            return false;
        }

        const lst = this.props.entity.StorageSpaces.filter(f => Helper.isNullOrEmpty(f.Designation) || Helper.isNullOrEmpty(f.Number))
        if (lst.length > 0) {
            NotificationHelper.showWarningMessage(Helper.getTranslation("WebSMS.Warehouse.CheckStorageSpaces"));
            return false;
        }

        return true;
    }

    save = async () => {
        if (!this.validate()) {
            return;
        }
        const result = await HTTPService.post<JsonResultClass<Warehouse>>("Warehouse/Save", { warehouse: this.props.entity });
        if (result.Code == 0) {
            if (this.props.entity.IsNew) {
                this.props.entity.IsNew = false;
                this.props.entity.Id = result.Data.Id;
                General.Functions.HistoryState.replaceState("Warehouse/Edit?id=" + this.props.entity.Id);
            }
            NotificationHelper.showSuccessMessage(Helper.getTranslation("Standard.Msg.SpeichernErfolgreich"));
        } else {
            NotificationHelper.showErrorMessage(result.Message);
        }
        this.common.handlePostSave(result.Code === 0, false);
        this.setState({});
    }

    remove = async () => {
        const isOk = await DialogHelper.confirm(Helper.getTranslation("Standard.Msg.Loeschen"));
        if (!isOk)
            return;

        const { Code, Message } = await HTTPService.get<JsonResultClass>(`Warehouse/Delete?lagerId=${this.props.entity.Id}`);
        if (Code == 0) {
            NotificationHelper.showSuccessMessage(Helper.getTranslation("Ressource.LöschenErfolgreich"));
            this.props.entity.RecordRight.CanWrite = false;
            this.setState({});
            this.common.sheetWrapper.trigger(SheetEventType.ItemDeleted, this.props.entity);
            const count = General.Sheet.Manager.getSheetsCount();
            if (count == 1) {
                setTimeout(() => {
                    window.location.href = Config.baseUrl + "DynamicSearch/Warehouse";
                }, 1000);
            } else {
                const sheet = General.Sheet.Manager.getTopMostSheet();
                sheet.hide();
            }
        } else {
            NotificationHelper.showErrorMessage(Message);
        }


    }

    render() {
        const props = $.extend({}, this.props, this.common.getHandlersForProps(), { apphelper: this.common, handleFieldChange: this.handleFieldChange });
        return <CommonApp {...props} />;
    }
}
interface StorageSpacesGridProps {
    lagerId: number;
    data: StorageSpace[];
    states: KeyValuePair<number, string>[];
    onChange: (data: StorageSpace[]) => void;
}
class StorageSpacesGrid extends React.Component<StorageSpacesGridProps, { nextId: number }> {
    private gridOptions;
    private NEW_BTN_ID = "new";
    private DELETE_BTN_ID = "delete";
    constructor(props: StorageSpacesGridProps) {
        super(props);
        this.state = {
            nextId: -1
        }
        this.initApp();
    }


    private initApp = async () => {
        this.setOptions();
        const id = await HTTPService.get("Warehouse/GetNextStorageSpaceId");
        this.setState({ nextId: id })
    }

    private setOptions = () => {
        this.gridOptions = {
            columns: [
                {
                    type: "textBox",
                    field: "Designation",
                    title: Helper.getTranslation("Standard.Bezeichnung"),
                    required: true

                },
                {
                    type: "textBox",
                    field: "Number",
                    title: Helper.getTranslation("Standard.Number"),
                    required: true
                },
                {
                    type: "numericTextBox",
                    field: "Height",
                    title: Helper.getTranslation("WebSMS.Warehouse.Height"),
                    min: 0,
                    width: 90
                },
                {
                    type: "numericTextBox",
                    field: "Width",
                    title: Helper.getTranslation("WebSMS.Warehouse.Width"),
                    min: 0,
                    width: 90
                },
                {
                    type: "numericTextBox",
                    field: "Depth",
                    title: Helper.getTranslation("WebSMS.Warehouse.Depth"),
                    min: 0,
                    width: 90
                },
                {
                    type: "numericTextBox",
                    field: "Weight",
                    title: Helper.getTranslation("WebSMS.Warehouse.Weight"),
                    min: 0,
                    width: 90
                },
                {
                    type: "dropDown",
                    field: "State",
                    title: Helper.getTranslation("Field.Adresse.status"),
                    template: (item: StorageSpace) => {
                        const state = this.props.states.find(st => st.Key == item.State)
                        if (state) {
                            return state.Value;
                        }
                        return item.State;
                    },
                    dataSource: "states",
                    dataTextField: "Value",
                    dataValueField: "Key",
                    optionLabel: "",
                    filterable: {
                        ui: this.filter
                    }
                }
            ]
        };
    }

    private filter = (element: any) => {
        element.kendoDropDownList({
            dataTextField: "Value",
            dataValueField: "Key",
            dataSource: this.props.states,
            optionLabel: Helper.getTranslation("Standard.BitteWählen")
        });
    }

    private onValueChange = (action, fieldName, dataItems) => {
        if (action != "itemchange" && fieldName != "result")
            return;
        const data = this.props.data;
        const index = data.findIndex(f => f.Id == dataItems[0].Id)
        data[index][fieldName] = dataItems[0][fieldName];
        this.props.onChange(data);
    }

    private handleButtonClick = async (p: InGridButtonClickParams) => {
        switch (p.id) {
            case this.NEW_BTN_ID:
                const newItem = this.createStorageSpace()
                p.actions.add(newItem);
                break;
            case this.DELETE_BTN_ID:
                const idsToDelete = await this.canDeleteStorageSpace(p.selectedItems.map(item => item.Id))
                const data = this.props.data.filter(f => idsToDelete.findIndex(id => id == f.Id) == -1);
                this.props.onChange(data)
                p.actions.deleteSelected(false);
                break;
        }
    }

    private canDeleteStorageSpace = async (ids: number[]): Promise<number[]> => {
        const { Data } = await HTTPService.post<JsonResultClass>("Warehouse/CanDeleteStorageSpaces", { lagerId: this.props.lagerId, storageSpaceIds: ids })
        const result = [];
        let showWarningMsg = false;
        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            if (Data[id]) {
                result.push(id)
            } else {
                showWarningMsg = true;
            }
        }
        if (showWarningMsg) {
            NotificationHelper.showWarningMessage(Helper.getTranslation("WebSMS.Warehouse.ItemAreInStock2"))
        }
        return result;
    }

    private createStorageSpace = () => {
        const newItem: StorageSpace = {
            Designation: "",
            Id: this.state.nextId,
            Number: "",
            State: Math.min(... this.props.states.map(s => s.Key)),
            Height: 0,
            Width: 0,
            Depth: 0,
            Weight: 0
        }
        this.setState({ nextId: this.state.nextId + 1 });
        const data = this.props.data;
        data.push(newItem);
        this.props.onChange(data);
        return newItem;
    }

    render() {
        const someId = "StorageSpaces";

        let schema = new Schema();
        schema.Id = someId;

        const whatever: any = {
            ...this.props,
            dataSources: {
                states: this.props.states
            }
        };

        const buttons: Array<InGridToolBarButton> = [
            { type: ToolBarInGridButtonType.NEW, id: this.NEW_BTN_ID },
            { type: ToolBarInGridButtonType.DELETE, id: this.DELETE_BTN_ID }
        ];


        return (
            <div style={{ height: "500px" }}>
                <EditableGrid data={this.props.data}
                    id={someId}
                    schema={schema}
                    gridOptions={this.gridOptions}
                    editable={true}
                    onDataChanged={this.onValueChange}
                    validatable={false}
                    groupable={false}
                    filterable={true}
                    buttons={buttons}
                    onButtonClick={this.handleButtonClick}
                    {...whatever}
                />
            </div>
        )
    }
}

interface ResourcesProps {
    resources: KeyValuePair<string, string>[];
    onChange: (resources: KeyValuePair<string, string>[]) => void;
}
class Resources extends React.Component<ResourcesProps, any>{
    private gridOptions: kendo.ui.GridOptions;
    private standardButtons: Array<ToolBarStandardButton> = [
        { type: ToolBarStandardButtonType.NEW, caption: Helper.getTranslation("WebSMS.Reminder.Add"), enabled: true },
        { type: ToolBarStandardButtonType.DELETE, caption: Config.translations.deleteLabel, enabled: true }
    ];
    constructor(props) {
        super(props)
        this.initApp();
    }

    private initApp = () => {
        this.gridOptions = {
            sortable: true,
            pageable: false,
            groupable: false,
            filterable: false,
            selectable: "multiple, row",
            resizable: true,
            scrollable: true,
            editable: false,
            columns: [
                {
                    field: "Value",
                    title: Helper.getTranslation("Ressource.Ressource"),
                }
            ]
        };
    }

    private onStandardClick = async (type: ToolBarStandardButtonType, dataItem) => {
        let data = this.props.resources
        switch (type) {
            case ToolBarStandardButtonType.NEW:
                const resources = await this.addResources()
                data = data
                    .concat(resources)
                    .filter((value, index, array) => {
                        return array.findIndex(f => f.Key == value.Key) === index;
                    });
                break;
            case ToolBarStandardButtonType.DELETE:
                const items = Array.isArray(dataItem) ? dataItem : [dataItem]
                data = data.filter(f1 => items.findIndex(f2 => f1.Key == f2.Key) == -1)
                break;
        }
        this.props.onChange(data);
    }

    private addResources = () => {
        return new Promise<KeyValuePair<string, string>[]>((resolve) => {
            const url = "DynamicSearch/Window/Resource?cus_disableRECHTPE=true&cus_disablePERSONALDATENRECHT=true&multi=true&resstatus=0%20or%20null&disabled=resstatus&displayPattern=[[name]] ([[ressnr]])"
            const sheet = General.Sheet.Manager.createSheetWithUrl(url, null, { disableTabTitle: true });
            sheet.bind(SheetEventType.ItemsSelected, (data) => {
                const resources: { ressnr, name, vorname }[] = data.data;
                const result: KeyValuePair<string, string>[] = resources.map((res) => {
                    const name = Helper.isNullOrEmpty(res.vorname) ? res.name : `${res.name}, ${res.vorname}`;
                    return {
                        Key: res.ressnr,
                        Value: name
                    }
                })
                resolve(result)
            });
        });
    }

    render() {
        const data = this.props.resources.sort((a, b) => {
            if (a.Value < b.Value) {
                return -1;
            } else if (a.Value > b.Value) {
                return 1;
            }
            return 0;
        })


        return (
            <div className="flex-container">
                <Grid
                    data={data}
                    options={this.gridOptions}
                    id={"warehouse_resources"}
                    standardButtons={this.standardButtons}
                    onClickStandardButton={this.onStandardClick}
                />
            </div>
        );
    }
}

interface StorageSpace {
    Id: number;
    State: number;
    Designation: string;
    Number: string;
    Height: number;
    Width: number;
    Depth: number;
    Weight: number;
}
interface Warehouse extends Entity {
    Id: number;
    StorageSpaces: StorageSpace[];
    Resources: KeyValuePair<string, string>[];
}